import { Router, NavigationExtras } from '@angular/router';
import { inject } from '@angular/core';

/**
 * Utility class for handling unauthorized access
 */
export class UnauthorizedAccessHandler {
  /**
   * Redirect to unauthorized page with context
   * @param attemptedUrl The URL the user tried to access
   * @param message Optional custom error message
   * @param router Router instance (optional, will be injected if not provided)
   */
  static redirect(
    attemptedUrl: string,
    message?: string,
    router?: Router
  ): void {
    const routerInstance = router || inject(Router);
    
    const navigationExtras: NavigationExtras = {
      state: {
        attemptedUrl,
        message: message || 'You do not have permission to access this resource.'
      }
    };

    routerInstance.navigate(['/unauthorized'], navigationExtras);
  }

  /**
   * Redirect with role-based message
   * @param attemptedUrl The URL the user tried to access
   * @param requiredRole The role required to access the resource
   * @param router Router instance (optional)
   */
  static redirectWithRole(
    attemptedUrl: string,
    requiredRole: string,
    router?: Router
  ): void {
    const message = `This page is only accessible to users with ${requiredRole} privileges.`;
    this.redirect(attemptedUrl, message, router);
  }

  /**
   * Redirect with authentication message
   * @param attemptedUrl The URL the user tried to access
   * @param router Router instance (optional)
   */
  static redirectAuthRequired(
    attemptedUrl: string,
    router?: Router
  ): void {
    const message = 'You must be signed in to access this resource.';
    this.redirect(attemptedUrl, message, router);
  }

  /**
   * Redirect with session expired message
   * @param attemptedUrl The URL the user tried to access
   * @param router Router instance (optional)
   */
  static redirectSessionExpired(
    attemptedUrl: string,
    router?: Router
  ): void {
    const message = 'Your session has expired. Please sign in again to continue.';
    this.redirect(attemptedUrl, message, router);
  }
}
